import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { convertLakemedelData } from "@/store/modules/lakemedel/helpers";
import { Lakemedel } from "@/tabs/Receptforskrivning/types";
import cloneDeep from "lodash/cloneDeep";

type State = BaseState & {
    lakemedel: Map<string, Lakemedel>;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Getters = {
    lakemedelById: (data: { id: string }) => Lakemedel | null;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        lakemedel: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "lakemedel";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Den här ska inte anropas direkt. Hämtning ska göras via actionen (fetchLakemedel)
         */
        lakemedel: (state: State) => [...state.lakemedel.values()],
        /**
         * Anropar en generisk getter för hämtning av subset. computed i en komponent som lyssnar på denna getter
         * är förmodligen vad du vill ha i din komponent.
         * Se helpers/baseSubset
         */
        lakemedelSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                limit: 0,
                state,
                collectionName: moduleName,
            }),
        /**
         * Getter med konverterade läkemedel för att passa formatet vi vill ha i FE. Det är troligen det här du vill
         * hämta från store
         */
        lakemedelDisplaySubset: (state: State) => (props: SubsetGetterProps<State>) => {
            const foundLakemedel = subsetGetter<State>({
                ...props,
                limit: 0,
                toArray: true,
                state,
                collectionName: moduleName,
            });

            return (foundLakemedel as Lakemedel[]).map((lakemedel) => convertLakemedelData({ lakemedel }));
        },
        /**
         * Om du bara vill hämta ett objekt vid ett enda tillfälle så ska det göras via actionen och inte här
         * (getLakemedelById, som hämtar från BE om objektet inte finns i store).
         * Om du vill lyssna på förändringar på ett enskilt objekt så vill du troligen lyssna på den här i en
         * computed i din komponent
         */
        lakemedelById:
            (state: State) =>
            ({ id, displayMode }: { id?: Lakemedel["id"]; displayMode?: boolean }) => {
                if (!id) {
                    return null;
                }

                /**
                 * Omvandlar det som hämtas till ett format som är mer anpassat till visning istället för
                 * originalnamnen från NLL
                 */
                if (displayMode) {
                    return convertLakemedelData({ lakemedel: state.lakemedel.get(id) });
                }

                return state.lakemedel.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Tillägg av nya objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (createLakemedel)
         * Se helpers/subset
         */
        addLakemedel: (
            state: State,
            {
                offset,
                subsetName = "default",
                lakemedel = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                lakemedel: Lakemedel[];
            }
        ) =>
            subsetSetter<State, Lakemedel>({
                offset,
                subsetName,
                state,
                collectionName: moduleName,
                items: lakemedel,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        getLakemedelById: async (
            {
                getters,
                commit,
                state,
                rootGetters,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                display = true,
            }: {
                id: Lakemedel["id"];
                display?: boolean;
            }
        ) => {
            const foundLakemedel = getters.lakemedelById({ id });

            if (foundLakemedel) {
                return display ? convertLakemedelData({ lakemedel: foundLakemedel }) : foundLakemedel;
            }

            try {
                const sessionData = rootGetters["nll/sessionData"];

                const { data } = await klinikenApi.get<Lakemedel>(
                    `/lakemedel/${id}`,
                    getConfig({
                        headers: rootGetters["nll/headers"],
                    })
                );

                if (data) {
                    commit("addLakemedel", {
                        offset: 0,
                        lakemedel: [data],
                        subsetName: id,
                    });

                    return display ? convertLakemedelData({ lakemedel: data }) : data;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta läkemedel från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den skapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchLakemedel: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 0,
                page = 1,
                filterParams,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Lakemedel>({
                    filterParams,
                    state,
                    limit,
                    page,
                    collectionName: moduleName,
                    apiUrl: "sil/drugsearch/",
                });

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addLakemedel", {
                    offset,
                    subsetName,
                    lakemedel: results.map((lakemedel) => ({ ...lakemedel, id: lakemedel.nplPackId })),
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
